import type { TranslationDictionary } from "../types";

const en: TranslationDictionary = {
  // ── Nav ──────────────────────────────────────────────────────────────
  "nav.properties": "Properties",
  "nav.outreach": "Outreach",
  "nav.settings": "Settings",
  "nav.toggleTheme": "Toggle theme",
  "nav.dashboard": "Dashboard",
  "nav.taches": "Tasks",
  "nav.valides": "Validated",
  "nav.archives": "Archives",
  "nav.aArbitrer": "To arbitrate",
  "nav.mesSms": "My SMS",

  // ── My SMS (Feature #6 : per-commercial customization) ───────────────
  "mesSms.title": "My auto SMS",
  "mesSms.subtitle": "Customize the 2 messages sent as first contact. The system alternates between them at each send (anti-spam diversity).",
  "mesSms.captationLabel": "Message #1 — Direct offer",
  "mesSms.captationDescription": "Direct style: present the offer (renting to associations).",
  "mesSms.accrocheLabel": "Message #2 — Open question",
  "mesSms.accrocheDescription": "Open question style: engage with the seller's listing.",
  "mesSms.placeholderHint": "Available placeholder:",
  "mesSms.placeholderExplain": "will be automatically replaced by your first name when sending.",
  "mesSms.limitHint": "Ideally ≤ {count} characters: beyond, the SMS may be split in multiple segments and arrive out of order.",
  "mesSms.chars": "characters",
  "mesSms.warnSplit": "SMS split risk",
  "mesSms.warnMax": "max {max} reached",
  "mesSms.badgeCustom": "Custom version",
  "mesSms.badgeDefault": "Default version",
  "mesSms.resetDefault": "Reset",
  "mesSms.save": "Save",
  "mesSms.saving": "Saving...",
  "mesSms.saved": "Templates saved",
  "mesSms.saveFailed": "Save failed",

  // ── Dashboard ────────────────────────────────────────────────────────
  "dashboard.title": "Dashboard",
  "dashboard.subtitle": "Property Pipeline Overview",
  "dashboard.properties": "Properties",
  "dashboard.listings": "Listings",
  "dashboard.withPhone": "With Phone",
  "dashboard.new24h": "New (24h)",
  "dashboard.new7d": "New (7d)",
  "dashboard.needsReview": "Needs Review",
  "dashboard.scheduler": "Scheduler",
  "dashboard.outreach": "Outreach",
  "dashboard.pipeline": "Pipeline",
  "dashboard.active": "{count} active",
  "dashboard.idle": "Idle",
  "dashboard.lastRun": "Last run",
  "dashboard.on": "On",
  "dashboard.off": "Off",
  "dashboard.recentRuns": "Recent Runs",
  "dashboard.pipelineRun": "Pipeline run",
  "dashboard.found": "found",
  "dashboard.new": "new",
  "dashboard.noRuns": "No runs yet",
  "dashboard.viewAllRuns": "View all runs →",
  "dashboard.dbError": "Unable to connect to the database. Check your connection and refresh the page.",
  "dashboard.next": "next {time}",

  // ── Charts ───────────────────────────────────────────────────────────
  "chart.costBreakdown": "Cost Breakdown (30d)",
  "chart.noCostData": "No cost data recorded yet.",
  "chart.sourceBreakdown": "Source Breakdown",
  "chart.outreachFunnel": "Outreach Funnel",
  "chart.matchingDistribution": "Matching Distribution",
  "cost.apify": "Apify",
  "cost.openai": "OpenAI",
  "cost.sms": "SMS",
  "cost.playwright": "Playwright",

  // ── Properties ───────────────────────────────────────────────────────
  "properties.title": "Properties",
  "properties.allProperties": "All Properties",
  "properties.unmatched": "Unmatched",
  "properties.falseNegatives": "False Negatives",
  "properties.pipeline": "Pipeline",
  "properties.overview": "Overview",
  "properties.sources": "Sources",
  "properties.total": "Total",
  "properties.pending": "New",
  "properties.contacted": "Contacted",
  "properties.withPhone": "With Phone",
  "properties.noPhone": "No Phone",
  "properties.needsReview": "Needs Review",

  // ── Properties Board ─────────────────────────────────────────────────
  "board.searchPlaceholder": "Search title, city, postal, phone...",
  "board.allStatuses": "All statuses",
  "board.allSources": "All sources",
  "board.allDepartments": "All departments",
  "board.department": "Department",
  "board.all": "All",
  "board.withPhone": "With phone ({count})",
  "board.noPhone": "No phone ({count})",
  "board.needsReview": "Needs Review ({count})",
  "board.noProperties": "No properties found.",
  "board.noLinkedListings": "No linked listings.",
  "board.loading": "Loading...",
  "board.title": "Title",
  "board.price": "Price",
  "board.surface": "Surface",
  "board.rooms": "Rooms",
  "board.city": "City",
  "board.phone": "Phone",
  "board.outreach": "Outreach",
  "board.lastSeen": "Last Seen",
  "board.sortByListings": "Sort by listings",
  "board.delete": "Delete {count}",
  "board.deleteConfirm": "Delete {count} {noun}? This cannot be undone.",
  "board.deleted": "Deleted {count} {noun}",
  "board.archive": "Archive {count}",
  "board.archiveConfirm": "Archive {count} {noun}? They will no longer be proposed for outreach but stay visible in the archives.",
  "board.archived": "Archived {count} {noun}",
  "board.anyAge": "Any age",
  "board.olderThan7Days": "Older than 7 days",
  "board.olderThan15Days": "Older than 15 days",
  "board.olderThan30Days": "Older than 30 days",
  "board.olderThan60Days": "Older than 60 days",
  "board.propertyOne": "property",
  "board.propertyMany": "properties",
  "board.propertiesCount": "{count} {noun}",
  "board.page": "Page {page} of {totalPages} ({total} total)",
  "board.previous": "Previous",
  "board.next": "Next",
  "board.untitled": "Untitled",
  "board.status": "Status",

  // ── Property statuses (PropertyStatus enum) ───────────────────────────
  "status.pending": "New",
  "status.manual_pending": "Awaiting manual handling",
  "status.manual_phone_fixe": "Landline - call manually",
  "status.contacted": "Contacted",
  "status.followup_1": "Follow-up 1",
  "status.followup_2": "Follow-up 2",
  "status.positive_return": "Positive return",
  "status.validated": "Validated",
  "status.archived": "Archived",
  "status.blacklisted": "Blacklisted",

  // ── Archive reasons ───────────────────────────────────────────────────
  "archiveReason.negative_return": "Negative return",
  "archiveReason.inactivity": "Inactivity",
  "archiveReason.manual": "Manual",

  // ── Outreach / Interaction statuses ──────────────────────────────────
  "status.responded": "responded",
  "status.rejected": "rejected",
  "status.closed": "closed",
  "status.sent": "sent",
  "status.failed": "failed",
  "status.noResponse": "no response",
  "status.qualified": "qualified",
  "status.manualRequired": "Manual required",
  "status.pendingSend": "Pending",
  "status.interested": "Interested",
  "status.notInterested": "Not Interested",
  "status.pendingReview": "To validate",
  "status.running": "running",
  "status.completed": "completed",
  "status.cancelled": "cancelled",

  // ── Commercial Dashboard ──────────────────────────────────────────────
  "commercial.dashboardTitle": "Commercial Dashboard",
  "commercial.lastWeek": "Last 7 days",
  "commercial.lastMonth": "Last 30 days",
  "commercial.week": "Week",
  "commercial.month": "Month",
  "commercial.sectionActivity": "Activity",
  "commercial.sectionOutcomes": "Outcomes",
  "commercial.sectionCurrent": "Current state",
  "commercial.scraped": "Scraped",
  "commercial.smsSent": "SMS sent",
  "commercial.followupSent": "Follow-ups",
  "commercial.transformationRate": "Transformation rate",
  "commercial.positiveReturns": "Positive returns",
  "commercial.negativeReturns": "Negative returns",
  "commercial.validated": "Validated",
  "commercial.activePipeline": "Active pipeline",
  "commercial.manualPending": "Manual sends",
  "commercial.fnPending": "Duplicates to review",

  // ── Property Detail ──────────────────────────────────────────────────
  "detail.backToProperties": "Back to properties",
  "detail.needsReview": "Needs review",
  "detail.price": "Price",
  "detail.surface": "Surface",
  "detail.rooms": "Rooms",
  "detail.bedrooms": "Bedrooms",
  "detail.city": "City",
  "detail.postalCode": "Postal code",
  "detail.gps": "GPS",
  "detail.phone": "Phone",
  "detail.firstSeen": "First seen",
  "detail.lastSeen": "Last seen",
  "detail.photosMatching": "{count} photo{s} matching across listings",
  "detail.relatedByPhone.title": "This phone is also linked to {count} other propert{ies}",
  "detail.relatedByPhone.subtitle": "Likely the same contact (agency, multi-property landlord). To avoid over-soliciting, only one property per phone is contacted automatically.",
  "detail.relatedByPhone.tag.duplicate": "Archived (phone duplicate)",
  "detail.relatedByPhone.noCity": "No city",
  "detail.linkedListings": "Linked Listings ({count})",
  "detail.noLinkedListings": "No linked listings.",
  "detail.original": "Original",
  "detail.photoMatch": "Photo match",
  "detail.partialPhoto": "Partial photo",
  "detail.criteriaOnly": "Criteria only",
  "detail.manual": "Manual",
  "detail.photosMatch": "{count} photo{s} match",
  "detail.review": "Review",
  "detail.manuallyConfirmed": "Manually confirmed",
  "detail.manualSeparation": "Manual separation",
  "detail.confirm": "Confirm",
  "detail.reject": "Reject",
  "detail.hashes": "Hashes ({count}):",
  "detail.none": "none",
  "detail.scraped": "Scraped",
  "detail.photos": "{count} photo{s}",
  "detail.outreach": "Outreach ({count})",
  "detail.reason": "Reason",
  "detail.model": "Model",
  "detail.prompt": "Prompt",

  // ── Interaction channels ─────────────────────────────────────────────
  "channel.sms": "SMS",
  "channel.formPap": "PAP Form",
  "channel.formSeloger": "SeLoger Form",
  "channel.formLogicimmo": "Logic-Immo Form",
  "channel.formBienici": "Bien Ici Form",
  "channel.formLefigaro": "LeFigaro Form",
  "channel.manual": "Manual",

  // ── Unmatched ────────────────────────────────────────────────────────
  "unmatched.source": "Source",
  "unmatched.title": "Title",
  "unmatched.price": "Price",
  "unmatched.surface": "Surface",
  "unmatched.rooms": "Rooms",
  "unmatched.city": "City",
  "unmatched.phone": "Phone",
  "unmatched.property": "Property",
  "unmatched.scraped": "Scraped",
  "unmatched.yes": "Yes",
  "unmatched.noListings": "No listings found.",
  "unmatched.listingCount": "{count} listing{s}",
  "unmatched.deleteSelected": "Delete {count} selected",
  "unmatched.deleteConfirm": "Delete {count} listing{s}? This cannot be undone.",
  "unmatched.deleted": "Deleted {count} listing{s}",
  "unmatched.page": "Page {page} of {totalPages}",
  "unmatched.previous": "Previous",
  "unmatched.next": "Next",

  // ── False Negatives ──────────────────────────────────────────────────
  "fn.title": "False-Negative Review",
  "fn.subtitle": "Find property pairs that should be merged but were missed by the matcher.",
  "fn.scan": "Scan",
  "fn.scanning": "Scanning…",
  "fn.propertiesScanned": "{count} properties scanned",
  "fn.pairsEvaluated": "{count} pairs evaluated",
  "fn.candidates": "{count} candidates",
  "fn.noCandidates": "No false-negative candidates found. All properties appear correctly separated.",
  "fn.reviewing": "Reviewing {current} of {total}",
  "fn.merged": "{count} merged",
  "fn.rejected": "{count} rejected",
  "fn.skipped": "{count} skipped",
  "fn.reviewComplete": "Review complete",
  "fn.candidatesReviewed": "{count} candidate{s} reviewed",
  "fn.scanAgain": "Scan again",
  "fn.skip": "Skip",
  "fn.merge": "Merge",
  "fn.moreDetails": "More details",
  "fn.less": "Less",
  "fn.untitledProperty": "Untitled property",
  "fn.listing": "{count} listing{s}",
  "fn.seen": "Seen {from} → {to}",
  "fn.noPhotos": "No photos",
  "fn.mergeSuccess": "Merged: {count} listing(s) moved",
  "fn.rejectSuccess": "Rejected — won't show again",

  // ── Outreach Hub ─────────────────────────────────────────────────────
  "outreach.title": "Outreach",
  "outreach.automated": "Automated",
  "outreach.outreachPipeline": "Outreach Pipeline",
  "outreach.total": "{count} total",
  "outreach.failed": "{count} failed",
  "outreach.noResponse": "{count} no response",
  "outreach.copyMessage": "Copy Message",
  "outreach.copied": "Copied",
  "outreach.messageCopied": "Message copied to clipboard",
  "outreach.markAsSent": "Mark as Sent",
  "outreach.markedAsSent": "Marked as sent",
  "outreach.failedUpdate": "Failed to update status",
  "outreach.viewProperty": "View Property",
  "outreach.untitledProperty": "Untitled property",
  "outreach.room": "{count} room{s}",
  "outreach.interactions": "Interactions",
  "outreach.notInterested": "{count} not interested",
  "outreach.recordResponse": "Record Response",
  "outreach.responsePlaceholder": "Enter the response received...",
  "outreach.submitResponse": "Submit",
  "outreach.responseRecorded": "Response recorded",
  "outreach.failedRecordResponse": "Failed to record response",
  "outreach.retryAsManual": "Retry as Manual",
  "outreach.retriedAsManual": "Moved to manual queue",
  "outreach.failureReason": "Failure reason",
  "outreach.failureReason.listing_unavailable": "Listing no longer available",
  "outreach.failureReason.apify_empty_dataset": "No listings found during search",
  "outreach.failureReason.apify_run_failed": "Listing search failed",
  "outreach.failureReason.apify_run_timed_out": "Listing search took too long",
  "outreach.failureReason.apify_run_aborted": "Listing search was interrupted",
  "outreach.failureReason.apify_form_failed": "Contact form could not be submitted",
  "outreach.failureReason.missing_listing_url": "Listing link not found",
  "outreach.failureReason.missing_form_context": "Not enough information to contact the owner",
  "outreach.failureReason.make_scenario_did_not_complete": "Message could not be sent",
  "outreach.failureReason.numero_fixe": "Landline number — cannot send SMS",
  "outreach.failureReason.landline_number": "Landline number — cannot send SMS",
  "outreach.failureReason.sms_not_sent": "SMS could not be sent",
  "outreach.failureReason.manual_fallback": "Automatic send failed — needs manual action",
  "outreach.failureReason.manual_fallback.form_seloger": "SeLoger form failed — needs manual action",
  "outreach.failureReason.manual_fallback.form_pap": "PAP form failed — needs manual action",
  "outreach.failureReason.manual_fallback.sms": "SMS send failed — needs manual action",
  "outreach.failureReason.generic": "Send failed — please retry or contact an administrator",
  "outreach.failureReason.no_automated_channel_available": "No automatic channel available. Enter a phone number and click \"Send SMS\", or copy the message and paste it into the platform to contact the owner manually.",
  "outreach.failureReason.no automated channel available": "No automatic channel available. Enter a phone number and click \"Send SMS\", or copy the message and paste it into the platform to contact the owner manually.",
  "outreach.smsSending": "Sending SMS…",
  "outreach.sendFailed": "Failed to send",
  "outreach.networkError": "Network error",
  "outreach.copyFailed": "Could not copy message",
  "outreach.enterPhone": "Enter a phone number",
  "outreach.relaunchSms": "Send as SMS",
  "outreach.openListing": "Open on {source}",
  "outreach.pasteHint": "paste in {source} messaging",
  "outreach.markingSent": "Saving…",
  "outreach.foundPhone": "I found a phone number",
  "outreach.stale3d": "3d no reply",
  "outreach.stale5d": "5d+ no reply",
  "outreach.outgoingMessage": "Outgoing message",
  "outreach.response": "Response",
  "outreach.confidence": "Confidence",
  "outreach.classification": "Classification",
  "outreach.regenerateMessage": "Regenerate",
  "outreach.regenerating": "Regenerating...",
  "outreach.messageRegenerated": "Message regenerated",
  "outreach.failedRegenerate": "Failed to regenerate message",
  "outreach.regenerateAll": "Regenerate all",
  "outreach.regeneratingAll": "Regenerating all...",
  "outreach.allRegenerated": "Messages regenerated ({count})",
  "outreach.regenerateQueued": "Regeneration queued — messages will update shortly",
  "outreach.viewPrompt": "View prompt",
  "outreach.testBadge": "Test",
  "outreach.testBadgeDetail": "Test Mode",

  // ── Outreach Auto ────────────────────────────────────────────────────
  "outreachAuto.searchPlaceholder": "Search property...",
  "outreachAuto.allChannels": "All channels",
  "outreachAuto.allStatuses": "All statuses",
  "outreachAuto.interactionCount": "{count} interaction{s}",
  "outreachAuto.propertyCol": "Property",
  "outreachAuto.phoneCol": "Phone",
  "outreachAuto.channelCol": "Channel",
  "outreachAuto.statusCol": "Status",
  "outreachAuto.dateCol": "Date",
  "outreachAuto.noInteractions": "No automated interactions found",
  "outreachAuto.noMessageContent": "No message content.",
  "outreachAuto.page": "Page {page} of {totalPages} ({total} total)",
  "outreachAuto.previous": "Previous",
  "outreachAuto.next": "Next",
  "outreachAuto.messageCopied": "Message copied",
  "outreachAuto.sent": "Sent",

  // ── Settings ─────────────────────────────────────────────────────────
  "settings.title": "Settings",
  "settings.runs": "Runs",
  "settings.scraping": "Scraping",
  "settings.prospection": "Prospection",
  "settings.arguments": "Arguments",
  "settings.blacklist": "Blacklist",
  "settings.language": "Language",

  // ── Run Controls ─────────────────────────────────────────────────────
  "runControls.scraping": "Scraping:",
  "runControls.prospection": "Prospection:",
  "runControls.active": "Active",
  "runControls.disabled": "Disabled",
  "runControls.lastRun": "Last run:",
  "runControls.nextRun": "Next run:",
  "runControls.sources": "{enabled}/{total} sources",
  "runControls.flagTestMode": "Test mode on",
  "runControls.flagFailedRuns": "{count} failed run{s} (24h)",
  "runControls.flagNoSources": "No active sources",

  // ── Runs Table ───────────────────────────────────────────────────────
  "runs.all": "All",
  "runs.status": "Status",
  "runs.task": "Task",
  "runs.source": "Source",
  "runs.started": "Started",
  "runs.duration": "Duration",
  "runs.found": "Found",
  "runs.new": "New",
  "runs.noRuns": "No pipeline runs found.",
  "runs.runCount": "{count} run{s}",
  "runs.page": "Page {page} of {totalPages}",
  "runs.previous": "Previous",
  "runs.next": "Next",
  "runs.pipelineSteps": "Pipeline steps:",
  "runs.error": "Error: ",
  "runs.errors": "Errors: ",
  "runs.result": "Result: ",
  "runs.payload": "Payload: ",
  "runs.refetchDataset": "Re-fetch Dataset",
  "runs.refetching": "Re-fetching...",
  "runs.cancel": "Cancel",
  "runs.cancelling": "Cancelling...",
  "runs.runCancelled": "Run cancelled",

  // ── Runs Page Client ─────────────────────────────────────────────────
  "runsPage.matchUnmatched": "Match unmatched listings",
  "runsPage.moreActions": "More actions",
  "runsPage.hashMissing": "Hash missing photos",
  "runsPage.recalculateHashes": "Recalculate all hashes",
  "runsPage.enrichUnenriched": "Enrich unenriched listings",
  "runsPage.matchJobQueued": "Matching job queued (run {id}...)",
  "runsPage.failedQueueMatch": "Failed to queue matching job",
  "runsPage.noListingsToHash": "No listings to hash",
  "runsPage.hashJobQueued": "Queued {count} listing{s} for hashing",
  "runsPage.failedQueueHash": "Failed to queue hashing",
  "runsPage.enrichJobQueued": "Enrichment job queued (run {id}...)",
  "runsPage.failedQueueEnrich": "Failed to queue enrichment",

  // ── Schedule Config ──────────────────────────────────────────────────
  "scheduleConfig.title": "Schedule",
  "scheduleConfig.enableAutomatic": "Enable automatic scheduling",
  "scheduleConfig.startHour": "Start hour",
  "scheduleConfig.runEvery": "Run every",
  "scheduleConfig.hours": "{count} hour{s}",
  "scheduleConfig.lastRun": "Last run: ",
  "scheduleConfig.nextRun": "Next run: ",
  "scheduleConfig.never": "Never",
  "scheduleConfig.notScheduled": "Not scheduled",
  "scheduleConfig.save": "Save",
  "scheduleConfig.saving": "Saving...",
  "scheduleConfig.runNow": "Run Now",
  "scheduleConfig.triggering": "Triggering...",
  "scheduleConfig.scheduleSaved": "Schedule saved",
  "scheduleConfig.triggered": "Triggered {count} pipeline run(s)",

  // ── Source Config ────────────────────────────────────────────────────
  "sourceConfig.title": "Source Configs",
  "sourceConfig.addSource": "Add Source",
  "sourceConfig.cancel": "Cancel",
  "sourceConfig.source": "Source",
  "sourceConfig.maxPages": "Max Pages",
  "sourceConfig.searchUrl": "Search URL",
  "sourceConfig.enabled": "Enabled",
  "sourceConfig.create": "Create",
  "sourceConfig.save": "Save",
  "sourceConfig.noConfigs": "No source configs yet. Add one to get started.",
  "sourceConfig.deleteTitle": "Delete source config?",
  "sourceConfig.deleteDescription": "This will permanently delete the {source} source config. This action cannot be undone.",
  "sourceConfig.delete": "Delete",
  "sourceConfig.pageCount": "{count} page{s}",
  "sourceConfig.unassigned": "Unassigned",
  "sourceConfig.noSourcesForUser": "No sources configured",
  "sourceConfig.department": "Department",
  "sourceConfig.user": "User",

  // ── Outreach Config ──────────────────────────────────────────────────
  "outreachConfig.title": "Outreach",
  "outreachConfig.enableAutomatic": "Enable automatic outreach",
  "outreachConfig.schedule": "Schedule",
  "outreachConfig.startHour": "Start hour",
  "outreachConfig.every": "Every",
  "outreachConfig.hours": "{count} hour{s}",
  "outreachConfig.defaultPromptLabel": "Default prompt:",
  "outreachConfig.userMessageLabel": "Data sent:",
  "outreachConfig.argumentsLabel": "{count} active argument{s} — 2 selected per run:",
  "outreachConfig.promptInstruction": "Prompt instruction",
  "outreachConfig.promptHelp": "Appended to the base prompt when generating outreach messages.",
  "outreachConfig.senderProfile": "Sender profile",
  "outreachConfig.firstName": "First name",
  "outreachConfig.lastName": "Last name",
  "outreachConfig.email": "Email",
  "outreachConfig.phone": "Phone",
  "outreachConfig.lastRun": "Last run: ",
  "outreachConfig.nextRun": "Next run: ",
  "outreachConfig.never": "Never",
  "outreachConfig.notScheduled": "Not scheduled",
  "outreachConfig.save": "Save",
  "outreachConfig.saving": "Saving...",
  "outreachConfig.runNow": "Run Now",
  "outreachConfig.triggering": "Triggering...",
  "outreachConfig.saved": "Outreach config saved",
  "outreachConfig.triggered": "Outreach routing triggered",
  "outreachConfig.failedTrigger": "Failed to trigger",
  "outreachConfig.enableHook": "Enable hook message (Step 1)",
  "outreachConfig.hookMessage": "SMS hook prompt",
  "outreachConfig.hookMessageHelp": "Prompt used to generate the first SMS (all sources). If empty, the default hook prompt is used.",
  "outreachConfig.hookMessagePlaceholder": "Role: SMS copywriter...\nMission: Write a short availability check SMS...\nConstraints: max 2 sentences...",
  "outreachConfig.enableFollowup": "Enable automatic follow-ups",
  "outreachConfig.followupPromptInstruction": "Follow-up 1 prompt",
  "outreachConfig.followupPromptHelp": "Instructions for generating the follow-up message. Leave blank to use the default prompt.",
  "outreachConfig.followup1Delay": "Follow-up 1 delay:",
  "outreachConfig.days": "days",
  "outreachConfig.enableFollowup2": "Enable 2nd follow-up",
  "outreachConfig.followup2Delay": "Follow-up 2 delay:",
  "outreachConfig.followup2PromptInstruction": "Follow-up 2 prompt",
  "outreachConfig.followup2PromptHelp": "Instructions for the 2nd follow-up. If empty, follow-up 1 prompt is used.",
  "outreachConfig.enableTestMode": "Enable test mode",
  "outreachConfig.testPhone": "Test phone number",
  "outreachConfig.testPhoneHelp": "In test mode, SMS are sent to this number only (1 per minute per channel). Test interactions are deleted when test mode is disabled.",
  "outreachConfig.formChannels": "Form Channels",
  "outreachConfig.enableFormSeloger": "Enable SeLoger form",
  "outreachConfig.enableFormPap": "Enable PAP form",
  "outreachConfig.formTestContact": "Form test contact",
  "outreachConfig.formTestHelp": "If empty, forms are skipped in test mode (only SMS sent).",
  "outreachConfig.formTestFirstName": "First name",
  "outreachConfig.formTestLastName": "Last name",
  "outreachConfig.formTestEmail": "Email",
  "outreachConfig.formTestPhone": "Phone",

  // ── Listing Modal ────────────────────────────────────────────────────
  "listingModal.untitled": "Untitled listing",
  "listingModal.viewOriginal": "View original listing",
  "listingModal.description": "Description",
  "listingModal.enrichedData": "Enriched Data",
  "listingModal.hashes": "Hashes ({count})",
  "listingModal.recalculate": "Recalculate",
  "listingModal.queuing": "Queuing...",
  "listingModal.noHashes": "No hashes computed yet.",
  "listingModal.details": "Details",
  "listingModal.scraped": "Scraped",
  "listingModal.created": "Created",
  "listingModal.updated": "Updated",
  "listingModal.sourceId": "Source ID",
  "listingModal.internalId": "Internal ID",
  "listingModal.fieldProvenance": "Field Provenance",
  "listingModal.linkedProperty": "Linked Property",
  "listingModal.photos": "{count} photo{s}",
  "listingModal.room": "Room",
  "listingModal.rooms": "Rooms",
  "listingModal.bedroom": "Bedroom",
  "listingModal.bedrooms": "Bedrooms",
  "listingModal.price": "Price",
  "listingModal.pricePerM2": "{price}/m²",
  "listingModal.rehashQueued": "Queued — hashes will update shortly",

  // ── Language ─────────────────────────────────────────────────────────
  "language.title": "Language",
  "language.description": "Choose the display language for the application.",
  "language.french": "Français",
  "language.english": "English",

  // ── Enriched field labels ────────────────────────────────────────────
  "enriched.floor": "Floor",
  "enriched.nb_floors": "Total floors",
  "enriched.bedrooms": "Bedrooms (AI)",
  "enriched.surface": "Surface (AI)",
  "enriched.surface_carrez": "Surface Carrez",

  // ── Provenance badges ────────────────────────────────────────────────
  "provenance.geocoded": "Geocoded",
  "provenance.ai": "AI",
  "provenance.scraped": "Scraped",
  "provenance.computed": "Computed",

  // ── Time ago ─────────────────────────────────────────────────────────
  "time.secondsAgo": "{count}s ago",
  "time.minutesAgo": "{count}m ago",
  "time.hoursAgo": "{count}h ago",
  "time.daysAgo": "{count}d ago",
  "time.today": "today",

  // ── FN detail columns ────────────────────────────────────────────────
  "fnDetail.bedrooms": "Bedrooms",
  "fnDetail.gps": "GPS",
  "fnDetail.firstLastSeen": "First / Last seen",
  "fnDetail.description": "Description",

  // ── Login ────────────────────────────────────────────────────────────
  "login.subtitle": "Sign in to your account",
  "login.password": "Password",
  "login.submit": "Sign in",
  "login.submitting": "Signing in…",
  "login.invalidCredentials": "Invalid credentials",
  "login.networkError": "Network error, please try again.",

  // ── Nav extras ───────────────────────────────────────────────────────
  "nav.home": "Home",
  "nav.team": "Team",
  "nav.logout": "Logout",

  // ── Tasks (taches) ───────────────────────────────────────────────────
  "taches.title": "Tasks",
  "taches.manualSend": "Manual send",
  "taches.duplicateCandidate": "Potential duplicate",
  "taches.matchToReview": "Match to review",
  "taches.markedSent": "Marked as sent",
  "taches.sendFailed": "Send failed",
  "taches.regenerateFailed": "Regeneration failed",
  "taches.messageRegenerated": "Message regenerated",
  "taches.messageCopied": "Message copied",
  "taches.taskCancelled": "Task cancelled",
  "taches.cancelFailed": "Cancellation failed",
  "taches.untitledProperty": "Untitled property",
  "taches.foundOn": "Found ",
  "taches.seenOn": "Seen ",
  "taches.propertyDetails": "Property details",
  "taches.viewProperty": "View property",
  "taches.message": "Message",
  "taches.regenerating": "Regenerating...",
  "taches.regenerate": "Regenerate",
  "taches.copied": "Copied",
  "taches.copy": "Copy",
  "taches.messagePlaceholder": "Message to send...",
  "taches.skip": "Skip",
  "taches.cancelling": "Cancelling...",
  "taches.listingUnavailable": "Listing unavailable",
  "taches.sending": "Sending...",
  "taches.markAsSent": "Mark as sent",
  "taches.merged": "Merged",
  "taches.rejected": "Rejected",
  "taches.actionFailed": "Action failed",
  "taches.highConfidence": "High confidence",
  "taches.mediumConfidence": "Medium confidence",
  "taches.lowConfidence": "Low confidence",
  "taches.samePhone": "Same phone",
  "taches.differentPhone": "Different phone",
  "taches.propertyA": "Property A",
  "taches.propertyB": "Property B",
  "taches.untitled": "Untitled",
  "taches.price": "Price",
  "taches.surface": "Surface",
  "taches.rooms": "Rooms",
  "taches.postalCode": "Postal code",
  "taches.phone": "Phone",
  "taches.photosA": "Photos A",
  "taches.photosB": "Photos B",
  "taches.rejecting": "Rejecting...",
  "taches.reject": "Reject",
  "taches.merging": "Merging...",
  "taches.merge": "Merge",
  "taches.matchConfirmed": "Match confirmed",
  "taches.matchSeparated": "Match separated",
  "taches.matchScore": "Match score: {score}",
  "taches.property": "Property",
  "taches.listing": "Listing ({source})",
  "taches.separating": "Separating...",
  "taches.separate": "Separate",
  "taches.confirming": "Confirming...",
  "taches.confirm": "Confirm",
  "taches.vacationBanner": "On leave — queue is read-only. Actions are disabled.",
  "taches.allDone": "All up to date!",
  "taches.noTasks": "No pending tasks.",
  "taches.duplicate": "Duplicate — score {score}",
  "taches.next": "Next",

  // ── Validated (valides) ──────────────────────────────────────────────
  "valides.title": "Validated properties",
  "valides.count": "{count} properties",
  "valides.empty": "No validated properties yet.",
  "valides.property": "Property",
  "valides.price": "Price",
  "valides.surface": "Surface",
  "valides.city": "City",
  "valides.validatedOn": "Validated on",
  "valides.notes": "Notes",
  "valides.notesSaved": "Notes saved",
  "valides.notesFailed": "Failed to save notes",
  "valides.notesPlaceholder": "Notes...",
  "valides.saving": "Saving...",
  "valides.untitled": "Untitled",
  "valides.actions": "Actions",
  "valides.edit": "Edit",
  "valides.archiveOne": "Archive this property",
  "valides.archiveSelected": "Archive {count} propert(y|ies)",
  "valides.archiveConfirm": "Archive {count} propert(y|ies)? They will no longer be in the validated list but stay in the archives.",
  "valides.archived": "Archived {count} propert(y|ies)",
  "valides.archiveFailed": "Archive failed",
  "valides.editTitle": "Edit property",
  "valides.updated": "Property updated",
  "valides.updateFailed": "Update failed",
  "valides.save": "Save",
  "valides.cancel": "Cancel",
  "valides.rooms": "Rooms",
  "valides.bedrooms": "Bedrooms",
  "valides.postalCode": "Postal code",
  "valides.phone": "Phone",

  // ── Comments thread ──────────────────────────────────────────────────
  "comments.title": "Notes & observations",
  "comments.loading": "Loading...",
  "comments.empty": "No comments yet.",
  "comments.placeholder": "Add an observation (call made, note, next step…)",
  "comments.add": "Add",
  "comments.saving": "Saving...",
  "comments.edit": "Edit",
  "comments.delete": "Delete",
  "comments.deleteConfirm": "Delete this comment?",
  "comments.systemAuthor": "System (import)",
  "comments.addFailed": "Failed to add comment",
  "comments.updateFailed": "Failed to update comment",
  "comments.deleteFailed": "Failed to delete comment",

  // ── Archives ─────────────────────────────────────────────────────────
  "archives.title": "Archives",
  "archives.count": "{count} properties",
  "archives.all": "All",
  "archives.descAll": "All archived properties",
  "archives.descNegative": "Refusal detected automatically by AI",
  "archives.descInactivity": "No activity for 2 months (automatic)",
  "archives.descManual": "Manually archived by a commercial",
  "archives.empty": "No archived properties found.",
  "archives.property": "Property",
  "archives.price": "Price",
  "archives.surface": "Surface",
  "archives.city": "City",
  "archives.reason": "Reason",
  "archives.archivedOn": "Archived on",
  "archives.untitled": "Untitled",

  // ── Manager dashboard ────────────────────────────────────────────────
  "manager.title": "Manager Dashboard",
  "manager.alertCount": "{count} commercial{s} need attention",
  "manager.period": "Period:",
  "manager.7days": "7 days",
  "manager.30days": "30 days",
  "manager.loading": "Loading...",
  "manager.noCommercials": "No commercials found.",
  "manager.commercial": "Commercial",
  "manager.dept": "Dept.",
  "manager.status": "Status",
  "manager.manualMessages": "Manual msgs",
  "manager.fnPending": "FN pending",
  "manager.scraped": "Scraped ({period})",
  "manager.smsSent": "SMS ({period})",
  "manager.statusUpdates": "Status upd. ({period})",
  "manager.validated": "Validated",
  "manager.transformRate": "Transform. rate",
  "manager.lastActivity": "Last activity",
  "manager.vacation": "Leave",
  "manager.alertActive": "Active alert",
  "manager.onVacation": "On leave",
  "manager.active": "Active",
  "manager.since": "since {date}",
  "manager.daysAgo": "{count}d ago",
  "manager.never": "never",
  "manager.resume": "Resume",
  "manager.setVacation": "Set on leave",
  "manager.alertLegend": "Active alert (task overdue >7d or inactivity >7d)",
  "manager.vacationLegend": "Alerts suppressed if commercial is on leave",
  "manager.fetchError": "Error {status}",
  "manager.unknownError": "Unknown error",
  "manager.toggleError": "Error toggling leave status",
  "manager.staleCount": "5d+ no reply",
  "manager.totalSent": "Sent",
  "manager.totalReplied": "Replies",
  "manager.responseRate": "Reply rate",

  // ── Outreach confirm dialog ───────────────────────────────────────────
  "outreach.regenerateConfirm": "Regenerate {count} messages? This will make {count} OpenAI calls.",

  // ── Property detail errors ────────────────────────────────────────────
  "detail.loadError": "Failed to load property",
  "detail.unknownError": "Unknown error",
};

export default en;
